import React, { useState, useEffect } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Search, X, FileText, FileSpreadsheet, Download, Upload, Trash2, Loader2, Pencil, Save, AlertTriangle } from "lucide-react";
import { PROVINCES, TOWNS, SERVICES } from "@/lib/services";

const BBBEE_LEVELS = ["Level 1", "Level 2", "Level 3", "Level 4", "Level 5", "Level 6", "Level 7", "Level 8", "Non-compliant"];
const DOC_SLOTS = ["SARS Tax Clearance", "B-BBEE Certificate", "COIDA Letter of Good Standing", "Company Registration Documents", "Proof of Address", "Company Profile"];
const ALL = "__all__";

const downloadBlob = async (path, filename) => {
  try {
    const res = await api.get(path, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  } catch (e) { toast.error("Export failed"); }
};

export default function AdminSmmeDatabase({ suppliers, sectors, reload }) {
  const blank = { company: "", keyword: "", province: ALL, sector: ALL, town: "", bbbee: ALL };
  const [draft, setDraft] = useState(blank);
  const [applied, setApplied] = useState(blank);
  const [edit, setEdit] = useState(null);
  const [del, setDel] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const setD = (k, v) => setDraft((f) => ({ ...f, [k]: v }));

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/admin/suppliers/${del.id}`);
      toast.success(`${del.company_name} removed`);
      setDel(null);
      reload();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setDeleting(false); }
  };

  const matches = (s) => {
    const inc = (val, q) => !q || String(val || "").toLowerCase().includes(q.trim().toLowerCase());
    const eq = (val, q) => q === ALL || String(val || "") === q;
    const kw = !applied.keyword || [s.company_name, s.description, s.sector, (s.services || []).join(" ")]
      .some((v) => String(v || "").toLowerCase().includes(applied.keyword.trim().toLowerCase()));
    return inc(s.company_name, applied.company) && kw && eq(s.province, applied.province)
      && eq(s.sector, applied.sector) && inc(s.town, applied.town) && eq(s.bbbee_level, applied.bbbee);
  };
  const filtered = suppliers.filter(matches);

  const doExport = (fmt) => {
    const p = new URLSearchParams();
    if (applied.company) p.set("company", applied.company);
    if (applied.keyword) p.set("keyword", applied.keyword);
    if (applied.province !== ALL) p.set("province", applied.province);
    if (applied.sector !== ALL) p.append("sector", applied.sector);
    if (applied.town) p.append("town", applied.town);
    if (applied.bbbee !== ALL) p.set("bbbee", applied.bbbee);
    const qs = p.toString();
    downloadBlob(`/export/suppliers?format=${fmt}${qs ? "&" + qs : ""}`, `smme-database.${fmt === "pdf" ? "pdf" : "xlsx"}`);
  };

  return (
    <div className="space-y-5">
      <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="admin-smme-filters">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div>
            <Label className="text-xs font-bold uppercase tracking-wide text-slate-500">Company name</Label>
            <Input value={draft.company} onChange={(e) => setD("company", e.target.value)} placeholder="Search company" className="mt-1.5" data-testid="admin-smme-company-input" />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wide text-slate-500">Keyword</Label>
            <Input value={draft.keyword} onChange={(e) => setD("keyword", e.target.value)} placeholder="Name, service or description" className="mt-1.5" data-testid="admin-smme-keyword-input" />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wide text-slate-500">Town / Village</Label>
            <Input value={draft.town} onChange={(e) => setD("town", e.target.value)} placeholder="Search town" className="mt-1.5" data-testid="admin-smme-town-input" />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wide text-slate-500">Province</Label>
            <Select value={draft.province} onValueChange={(v) => setD("province", v)}>
              <SelectTrigger className="mt-1.5" data-testid="admin-smme-province-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value={ALL}>All provinces</SelectItem>{PROVINCES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wide text-slate-500">Sector</Label>
            <Select value={draft.sector} onValueChange={(v) => setD("sector", v)}>
              <SelectTrigger className="mt-1.5" data-testid="admin-smme-sector-select"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72"><SelectItem value={ALL}>All sectors</SelectItem>{(sectors || []).map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wide text-slate-500">B-BBEE level</Label>
            <Select value={draft.bbbee} onValueChange={(v) => setD("bbbee", v)}>
              <SelectTrigger className="mt-1.5" data-testid="admin-smme-bbbee-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value={ALL}>All levels</SelectItem>{BBBEE_LEVELS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap gap-3">
          <Button onClick={() => setApplied(draft)} className="gap-2 bg-slate-900 hover:bg-slate-800" data-testid="admin-smme-search-button"><Search className="h-4 w-4" /> Search</Button>
          <Button variant="outline" onClick={() => { setDraft(blank); setApplied(blank); }} className="gap-2" data-testid="admin-smme-reset-button"><X className="h-4 w-4" /> Reset</Button>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm font-semibold text-slate-700" data-testid="admin-smme-count">{filtered.length} SMME{filtered.length === 1 ? "" : "s"} found</p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => doExport("pdf")} className="gap-1" data-testid="admin-export-smme-pdf"><FileText className="h-4 w-4 text-red-600" /> Export to PDF</Button>
            <Button variant="outline" onClick={() => doExport("xlsx")} className="gap-1" data-testid="admin-export-smme-excel"><FileSpreadsheet className="h-4 w-4 text-emerald-600" /> Export to Excel</Button>
          </div>
        </div>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-smme-table">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr><th className="px-4 py-3">Company</th><th className="px-4 py-3">Contact</th><th className="px-4 py-3">Province</th><th className="px-4 py-3">Sector</th><th className="px-4 py-3">BBBEE</th><th className="px-4 py-3">Docs</th><th className="px-4 py-3 text-right"></th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.length === 0 && <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-500" data-testid="admin-no-smme">No SMMEs match your filters.</td></tr>}
              {filtered.map((s) => (
                <tr key={s.id} className="hover:bg-slate-50" data-testid={`admin-smme-row-${s.id}`}>
                  <td className="px-4 py-3 font-medium text-slate-800">{s.company_name}<div className="text-xs font-normal text-slate-500">{s.email}</div></td>
                  <td className="px-4 py-3 text-slate-600">{s.contact_person || s.name || "—"}<div className="text-xs text-slate-400">{s.phone || ""}</div></td>
                  <td className="px-4 py-3 text-slate-600">{s.province || "—"}<div className="text-xs text-slate-400">{s.town || ""}</div></td>
                  <td className="px-4 py-3 text-slate-600">{s.sector || "—"}</td>
                  <td className="px-4 py-3 font-mono text-xs">{s.bbbee_level || "—"}</td>
                  <td className="px-4 py-3 font-mono">{(s.documents || []).length}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button size="sm" variant="outline" onClick={() => setEdit(s)} className="gap-1" data-testid={`admin-smme-edit-${s.id}`}><Pencil className="h-3.5 w-3.5" /> View / Edit</Button>
                      <Button size="sm" variant="ghost" onClick={() => setDel(s)} className="text-red-600 hover:bg-red-50 hover:text-red-700" data-testid={`admin-smme-delete-${s.id}`}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {edit && <AdminSmmeEditDialog supplier={edit} sectors={sectors} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); reload(); }} />}
      {del && (
        <Dialog open onOpenChange={() => !deleting && setDel(null)}>
          <DialogContent data-testid="admin-smme-delete-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 font-display"><AlertTriangle className="h-5 w-5 text-red-600" /> Delete SMME</DialogTitle>
              <DialogDescription>
                This permanently removes <span className="font-semibold text-slate-800">{del.company_name}</span> and all their quotes from the portal. This cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDel(null)} disabled={deleting} data-testid="admin-smme-delete-cancel">Cancel</Button>
              <Button onClick={confirmDelete} disabled={deleting} className="gap-1 bg-red-600 hover:bg-red-700" data-testid="admin-smme-delete-confirm">
                {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} Delete SMME
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function AdminSmmeEditDialog({ supplier, sectors, onClose, onSaved }) {
  const [current, setCurrent] = useState(supplier);
  const [form, setForm] = useState({
    company_name: supplier.company_name || "", contact_person: supplier.contact_person || supplier.name || "",
    email: supplier.email || "", phone: supplier.phone || "", province: supplier.province || "",
    town: supplier.town || "", sector: supplier.sector || "", bbbee_level: supplier.bbbee_level || "",
    registration_number: supplier.registration_number || "", tax_number: supplier.tax_number || "",
    description: supplier.description || "",
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState("");
  const [docType, setDocType] = useState(DOC_SLOTS[0]);
  const [expiry, setExpiry] = useState("");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const docs = current.documents || [];

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/suppliers/${current.id}`, form);
      setCurrent(data);
      toast.success("SMME profile saved");
      onSaved();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(docType);
    try {
      const fd = new FormData();
      fd.append("file", file); fd.append("doc_type", docType);
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const { data: fresh } = await api.post(`/admin/suppliers/${current.id}/documents`, { file_id: data.id, doc_type: docType, filename: data.original_filename, expiry_date: expiry || null });
      setCurrent(fresh); setExpiry("");
      toast.success(`${docType} uploaded`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploading(""); e.target.value = ""; }
  };

  const delDoc = async (fid) => {
    try { const { data } = await api.delete(`/admin/suppliers/${current.id}/documents/${fid}`); setCurrent(data); toast.success("Document removed"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const F = ({ k, label, mono }) => (
    <div>
      <Label className="text-xs uppercase tracking-wide text-slate-400">{label}</Label>
      <Input value={form[k]} onChange={(e) => set(k, e.target.value)} className={`mt-1 ${mono ? "font-mono" : ""}`} data-testid={`admin-edit-${k}`} />
    </div>
  );

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto" data-testid="admin-smme-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Edit SMME · {current.company_name}</DialogTitle>
          <DialogDescription>Update the SMME's details and compliance documents.</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <F k="company_name" label="Company name" />
          <F k="contact_person" label="Contact person" />
          <F k="email" label="Email" />
          <F k="phone" label="Phone" />
          <div>
            <Label className="text-xs uppercase tracking-wide text-slate-400">Province</Label>
            <Select value={form.province} onValueChange={(v) => set("province", v)}>
              <SelectTrigger className="mt-1" data-testid="admin-edit-province"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{PROVINCES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide text-slate-400">Town / Village</Label>
            <Input value={form.town} onChange={(e) => set("town", e.target.value)} list="admin-town-list" className="mt-1" data-testid="admin-edit-town" />
            <datalist id="admin-town-list">{TOWNS.map((t) => <option key={t} value={t} />)}</datalist>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide text-slate-400">Sector</Label>
            <Input value={form.sector} onChange={(e) => set("sector", e.target.value)} list="admin-sector-list" className="mt-1" data-testid="admin-edit-sector" />
            <datalist id="admin-sector-list">{[...(sectors || []), ...SERVICES].map((t) => <option key={t} value={t} />)}</datalist>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide text-slate-400">B-BBEE level</Label>
            <Select value={form.bbbee_level} onValueChange={(v) => set("bbbee_level", v)}>
              <SelectTrigger className="mt-1" data-testid="admin-edit-bbbee"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{BBBEE_LEVELS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <F k="registration_number" label="CIPC number" mono />
          <F k="tax_number" label="Tax number" mono />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wide text-slate-400">About / description</Label>
          <Textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={3} className="mt-1" data-testid="admin-edit-description" />
        </div>
        {(current.services || []).length > 0 && (
          <div>
            <Label className="text-xs uppercase tracking-wide text-slate-400">Services (from registration)</Label>
            <div className="mt-1 flex flex-wrap gap-1.5">{current.services.map((v) => <span key={v} className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-0.5 text-xs text-slate-700">{v}</span>)}</div>
          </div>
        )}

        <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-4">
          <Label className="text-xs font-bold uppercase tracking-wide text-slate-500">Compliance documents</Label>
          <div className="mt-2 divide-y divide-slate-100" data-testid="admin-edit-docs">
            {docs.length === 0 && <p className="py-2 text-sm text-slate-500">No documents uploaded.</p>}
            {docs.map((d) => (
              <div key={d.file_id} className="flex items-center justify-between py-2">
                <span className="flex items-center gap-2 text-sm text-slate-700"><FileText className="h-4 w-4 text-amber-600" /> {d.doc_type}{d.expiry_date ? ` · expires ${d.expiry_date}` : ""}</span>
                <div className="flex items-center gap-3">
                  <a href={`${api.defaults.baseURL}/files/${d.file_id}/download`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline" data-testid={`admin-doc-view-${d.file_id}`}><Download className="h-3.5 w-3.5" /> View</a>
                  <button onClick={() => delDoc(d.file_id)} className="text-red-600 hover:text-red-700" data-testid={`admin-doc-delete-${d.file_id}`}><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
            <Select value={docType} onValueChange={setDocType}>
              <SelectTrigger data-testid="admin-doc-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>{DOC_SLOTS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
            <Input type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)} data-testid="admin-doc-expiry-input" />
            <label className="inline-flex cursor-pointer items-center justify-center gap-2 rounded-lg bg-slate-700 px-3 py-2 text-sm font-semibold text-white hover:bg-slate-800" data-testid="admin-doc-upload-label">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} {uploading ? "Uploading…" : "Upload"}
              <input type="file" className="hidden" onChange={upload} data-testid="admin-doc-upload-input" />
            </label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="admin-edit-cancel">Close</Button>
          <Button onClick={save} disabled={saving} className="gap-1 bg-slate-900 hover:bg-slate-800" data-testid="admin-edit-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
